// Inspection.jsx

"use client";

import { useState, useEffect, useRef } from "react";
import Modal from "react-modal";
import SignatureCanvas from "react-signature-canvas";
import AuthenticatedLayout from "../../Layouts/AuthenticatedLayout";
import { TextField, Select, MenuItem, InputLabel, FormControl } from "@mui/material";
import { Send, X, ChevronLeft, ChevronRight, Upload, Undo } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

// Import components
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "../../Components/GroupedContainer";
import FormContainer from "../../Components/FormContainer";
import SecondaryButton from "../../Components/SecondaryButton";

// ✅ NEW: Factoring consent modal component
import FactoringConsentModal from "../../Components/FactoringConsentModal";

// Local diagrams (public folder)
const LOCAL_DIAGRAMS = {
  pickup: "/img/diagrams/pickupmodel.webp",
  sedan: "/img/diagrams/sedanmodel.webp",
  suv: "/img/diagrams/suvmodel.webp",
  van: "/img/diagrams/vanmodel.webp",
};

function Inspection() {
  const API_ORIGIN = "https://new.cajuu.app";

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const loadItemId = queryParams.get("load_item_id");

  const sigCanvas = useRef(null);
  const fileInputRef = useRef(null);
  const diagramRef = useRef(null);

  const navigate = useNavigate();

  // State management
  const [carouselModalIsOpen, setCarouselModalIsOpen] = useState(false);
  const [selectedCarouselImageIndex, setSelectedCarouselImageIndex] = useState(0);
  const [selectedRadioButton, setSelectedRadioButton] = useState(null);
  const [shapes, setShapes] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [load, setLoad] = useState(null);

  // ✅ profile (to know if driver is factoring-enabled)
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // ✅ factoring consent modal state
  const [isFactoringModalOpen, setIsFactoringModalOpen] = useState(false);
  const [isSavingFactoring, setIsSavingFactoring] = useState(false);
  const [factoringModalAutoOpened, setFactoringModalAutoOpened] = useState(false);

  // Boot loading (blocks page until diagram is ready)
  const [isLoading, setIsLoading] = useState(true); // loadItem fetch
  const [isDiagramReady, setIsDiagramReady] = useState(false); // diagram preload
  const [diagramError, setDiagramError] = useState("");

  // Submit/remote loader
  const [loading, setLoading] = useState(false);
  const [deliveryImage, setDeliveryImage] = useState(null);
  const MAIN_CARD_ID = "main";

  // Form data states
  const [odometer, setOdometer] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [drivable, setDrivable] = useState("Yes");
  const [windscreen, setWindscreen] = useState("Yes");
  const [intactGlass, setIntactGlass] = useState("Yes");
  const [paperwork, setPaperwork] = useState("Yes");
  const [keysQt, setKeysQt] = useState("");
  const [spareTire, setSpareTire] = useState("Yes");
  const [others, setOthers] = useState("");
  const [notes, setNotes] = useState("");
  const [images, setImages] = useState({
    main: {},
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Assinatura remota
  const [remoteSignatureLink, setRemoteSignatureLink] = useState("");
  const [isRequestingRemoteSignature, setIsRequestingRemoteSignature] = useState(false);
  const [remoteRequested, setRemoteRequested] = useState(false);

  // Helper pra converter Yes/No pra "1"/"0"
  const toNumericString = (value) => (value === "Yes" ? "1" : "0");

  // Helpers: somente inteiros >= 0
  const sanitizeNonNegativeIntInput = (value) => {
    const str = value ?? "";
    return String(str).replace(/[^\d]/g, "");
  };

  const coerceToNonNegativeIntString = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value).trim();
    if (str === "") return "";

    const n = Number(str);
    if (Number.isFinite(n)) {
      const coerced = Math.trunc(n);
      return String(Math.max(0, coerced));
    }

    // fallback (ex.: "123abc" -> "123")
    return sanitizeNonNegativeIntInput(str);
  };

  const isValidNonNegativeIntString = (value) => /^\d+$/.test(String(value));

  // Helpers: normalize and build URLs safely
  const normalizePath = (p) => {
    if (!p) return "";
    // handle backslashes just in case
    return String(p).trim().replace(/\\/g, "/");
  };

  const buildStorageUrl = (path) => {
    const clean = normalizePath(path);
    if (!clean) return "";

    if (/^https?:\/\//i.test(clean)) return clean;

    const normalized = clean.replace(/^\/+/, "");
    return `https://new.cajuu.app/storage/${normalized}`;
  };

  // Derived values
  const loadTransport = load?.load_transport || null;
  const loadTransportId = loadTransport?.id ?? null;

  const loadStatusId = loadTransport?.load_status_id;
  const isPickupInspection = loadStatusId === 2 || loadStatusId === 3; // Assigned (2) OU Confirmed (3)
  const isDeliveryInspection = loadStatusId === 4;

  // supports both snake_case and camelCase just in case
  const carTypeObj = load?.car_type || load?.carType || null;
  const carTypeLabel = carTypeObj?.type || "";

  const normalizeCarTypeKey = (typeValue) => {
    const raw = String(typeValue || "").trim().toLowerCase();
    const compact = raw.replace(/[^a-z]/g, ""); // remove spaces, hyphens, etc.

    if (!compact) return null;

    // Most specific first
    if (compact.includes("sedan")) return "sedan";
    if (compact.includes("suv")) return "suv";
    if (compact.includes("van")) return "van";

    // pickup can appear as "pickup", "pick-up", "pick up", "truck" etc.
    if (compact.includes("pickup") || compact.includes("pick") || compact.includes("truck")) return "pickup";

    return null;
  };

  const carTypeKey = normalizeCarTypeKey(carTypeLabel);
  const baseDiagramSrc = carTypeKey ? LOCAL_DIAGRAMS[carTypeKey] : null;

  // For delivery, show the stored inspection diagram (usually from Pickup) if available
  const diagramToDisplay = isDeliveryInspection
    ? deliveryImage || baseDiagramSrc
    : baseDiagramSrc;

  const loadItemLabel = load ? `${load.make} ${load.model} - ${load.year} [${carTypeLabel}]` : "";

  // ✅ Factoring eligibility rules (Task 2)
  const normalizePaymentType = (v) => String(v || "").trim().toUpperCase();

  const ELIGIBLE_PAYMENT_TYPES = ["COD", "2 BD", "5 BD", "7 BD", "15 BD", "30 BD"];

  const paymentType = loadTransport?.payment_type || "";
  const paymentRate = Number(loadTransport?.payment_rate || 0);

  const isPaymentTypeEligibleForFactoring = () => {
    const pt = normalizePaymentType(paymentType);
    if (!pt) return false;
    if (pt === "COP") return false; // ✅ explicitly excluded
    return ELIGIBLE_PAYMENT_TYPES.includes(pt);
  };

  const profileUserRoleId = Number(profile?.user_role_id ?? 0);
  const profileDriver = profile?.driver || null;
  const profileDriverId = profileDriver?.id ?? null;

  const isDriverFactoringEnabled = Number(profileDriver?.is_factoring_enabled ?? 0) === 1;
  const isUserEligibleRoleForFactoring = profileUserRoleId === 2 || profileUserRoleId === 4;

  const shouldOfferFactoring =
    !!loadTransportId &&
    !!profileDriverId &&
    isPickupInspection &&
    isUserEligibleRoleForFactoring &&
    isDriverFactoringEnabled &&
    isPaymentTypeEligibleForFactoring();

  const factoringLocalKey = () => {
    if (!profileDriverId || !loadTransportId) return null;
    return `factoring_consent_${profileDriverId}_${loadTransportId}`;
  };

  // Damage type options
  const radioOptions = [
    { value: "B", abbreviation: "B", label: "Bent" },
    { value: "BB", abbreviation: "BB", label: "Buffer Burned" },
    { value: "BR", abbreviation: "BR", label: "Broken" },
    { value: "C", abbreviation: "C", label: "Cut" },
    { value: "CR", abbreviation: "CR", label: "Cracked" },
    { value: "D", abbreviation: "D", label: "Dented" },
    { value: "L", abbreviation: "L", label: "Loose" },
    { value: "M", abbreviation: "M", label: "Missing" },
    { value: "PC", abbreviation: "PC", label: "Paint Chip" },
    { value: "R", abbreviation: "R", label: "Rubbed" },
    { value: "S", abbreviation: "S", label: "Scratched" },
    { value: "ST", abbreviation: "ST", label: "Stained" },
  ];

  // Initialize Modal
  useEffect(() => {
    Modal.setAppElement("#root");
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Fetch authenticated user profile (includes driver + is_factoring_enabled)
  useEffect(() => {
    const fetchProfile = async () => {
      setProfileLoading(true);
      try {
        const userToken = localStorage.getItem("token");
        const res = await axios.get(`${API_ORIGIN}/api/users/profile`, {
          headers: {
            "Screen-Name": "/InspectionLoadItem",
            Authorization: `Bearer ${userToken}`,
          },
        });

        const payload = res?.data?.data || res?.data || null;
        setProfile(payload);
      } catch (e) {
        setProfile(null);
      } finally {
        setProfileLoading(false);
      }
    };

    fetchProfile();
  }, [API_ORIGIN]);

  // Fetch load item data
  useEffect(() => {
    const fetchLoadItem = async () => {
      setIsLoading(true);
      setIsDiagramReady(false);
      setDiagramError("");

      // reset
      setLoad(null);
      setDeliveryImage(null);
      setShapes([]);
      setUndoStack([]);
      setSelectedRadioButton(null);

      try {
        const userToken = localStorage.getItem("token");

        const response = await axios.get(`https://new.cajuu.app/api/loaditem/${loadItemId}`, {
          headers: {
            "Screen-Name": "/InspectionLoadItem",
            Authorization: `Bearer ${userToken}`,
          },
        });

        const loadItem = response.data.data;
        setLoad(loadItem);

        const lt = loadItem.load_transport;

        // Dados de pickup (default)
        setCustomerName(lt.pickup_customer_name || "");
        setCustomerPhone(lt.pickup_customer_phone || "");
        setCustomerEmail(lt.pickup_customer_email || "");

        // Se for Delivery
        if (lt.load_status_id === 4) {
          const pickupInspection = loadItem.inspection_load_items?.find(
            (item) => item.inspection_type === "Pickup"
          );

          if (pickupInspection) {
            const storedOdometerValue = pickupInspection.odometer;
            if (storedOdometerValue !== null && storedOdometerValue !== undefined) {
              setOdometer(coerceToNonNegativeIntString(storedOdometerValue));
            }

            setCustomerName(
              lt.delivery_customer_name || lt.pickup_customer_name || ""
            );
            setCustomerPhone(
              lt.delivery_customer_phone || lt.pickup_customer_phone || ""
            );
            setCustomerEmail(
              lt.delivery_customer_email || lt.pickup_customer_email || ""
            );

            setDrivable(pickupInspection.drivable ? "Yes" : "No");
            setWindscreen(pickupInspection.windscreen ? "Yes" : "No");
            setIntactGlass(pickupInspection.intact_glass ? "Yes" : "No");
            setPaperwork(pickupInspection.paperwork ? "Yes" : "No");
            setKeysQt(coerceToNonNegativeIntString(pickupInspection.keys_qt));
            setSpareTire(pickupInspection.spare_tire ? "Yes" : "No");
            setOthers(pickupInspection.others || "");
          }

          // For Delivery, show the stored inspection diagram (usually from Pickup)
          const anyInspectionWithDiagram = loadItem.inspection_load_items?.find((i) => !!i?.diagram_path);

          const existingDiagramPath =
            pickupInspection?.diagram_path || anyInspectionWithDiagram?.diagram_path;

          if (existingDiagramPath) {
            const fullDeliveryImageURL = buildStorageUrl(existingDiagramPath);
            setDeliveryImage(fullDeliveryImageURL);
          } else {
            setDeliveryImage(null);
          }
        } else {
          setDeliveryImage(null);
        }
      } catch (error) {
        console.error("Error fetching load item data:", error);
        toast.error("Error loading data", {
          description: "Could not fetch vehicle information",
          duration: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (loadItemId) {
      fetchLoadItem();
    }
  }, [loadItemId]);

  // Preload diagram before showing the form (no wrong fallback flashes)
  const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
      if (!src) return reject(new Error("No src provided"));
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = (e) => reject(e || new Error("Image failed to load"));
      // cache-bust to avoid stale behavior during dev
      const hasQuery = String(src).includes("?");
      img.src = `${src}${hasQuery ? "&" : "?"}cb=${Date.now()}`;
    });
  };

  useEffect(() => {
    const run = async () => {
      // only after load fetched
      if (isLoading) return;

      // If load couldn't be fetched, don't block forever
      if (!load) {
        setIsDiagramReady(true);
        return;
      }

      // If car type not recognized, block with an explicit error (better than showing wrong diagram)
      if (!baseDiagramSrc) {
        const msg = `CarType "${carTypeLabel || "N/A"}" does not have a local diagram mapping.`;
        setDiagramError(msg);
        toast.error("Diagram not available", {
          description: msg,
          duration: 6000,
        });
        setIsDiagramReady(true);
        return;
      }

      // Decide which image should be shown
      const targetSrc = diagramToDisplay;

      try {
        setIsDiagramReady(false);
        await preloadImage(targetSrc);
        setIsDiagramReady(true);
      } catch (e) {
        console.error("Diagram preload error:", e);

        // If deliveryImage failed, fallback to correct local base diagram (same car type)
        if (isDeliveryInspection && deliveryImage && baseDiagramSrc && targetSrc === deliveryImage) {
          try {
            await preloadImage(baseDiagramSrc);
            setDeliveryImage(null); // force display of base diagram
            setIsDiagramReady(true);

            toast.error("Stored pickup diagram unavailable", {
              description: "Falling back to the correct local diagram for this vehicle type.",
              duration: 5000,
            });
            return;
          } catch (e2) {
            console.error("Base diagram preload also failed:", e2);
          }
        }

        // If base fails (should not), show error
        const msg = "Could not load the inspection diagram image.";
        setDiagramError(msg);
        toast.error("Diagram load failed", {
          description: msg,
          duration: 6000,
        });
        setIsDiagramReady(true);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, load, deliveryImage, isDeliveryInspection, baseDiagramSrc, diagramToDisplay, carTypeLabel]);

  // ✅ Auto-open factoring consent modal once per load/driver (Task 2)
  const showBootLoader = isLoading || !isDiagramReady;
  const showGlobalLoader = showBootLoader || loading || isRequestingRemoteSignature;

  useEffect(() => {
    if (showBootLoader) return;
    if (profileLoading) return;
    if (!loadTransportId) return;
    if (!profileDriverId) return;

    if (!shouldOfferFactoring) return;

    const key = factoringLocalKey();
    if (!key) return;

    const existing = localStorage.getItem(key);
    if (existing) return;

    if (factoringModalAutoOpened) return;

    setIsFactoringModalOpen(true);
    setFactoringModalAutoOpened(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showBootLoader,
    profileLoading,
    shouldOfferFactoring,
    loadTransportId,
    profileDriverId,
    factoringModalAutoOpened,
  ]);

  const persistFactoringDecision = (decision) => {
    const key = factoringLocalKey();
    if (!key) return;
    localStorage.setItem(key, String(decision));
  };

  const handleDeclineFactoring = () => {
    // persistFactoringDecision("declined");
    setIsFactoringModalOpen(false);
    toast.info("Factoring declined.", { duration: 2500 });
  };

  const handleAcceptFactoring = async () => {
    if (!loadTransportId) return;
    if (!profileDriverId) return;

    setIsSavingFactoring(true);

    try {
      const userToken = localStorage.getItem("token");

      // ✅ Endpoint será criado na Tarefa 3
      // Sugestão: POST /api/factoring (criar registro pending)
      const payload = {
        load_transport_id: loadTransportId,
        driver_id: profileDriverId,
        fee_percent: 5,
      };

      await axios.post(`${API_ORIGIN}/api/factoring`, payload, {
        headers: {
          "Screen-Name": "/InspectionLoadItem",
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "application/json",
        },
      });

      persistFactoringDecision("accepted");
      setIsFactoringModalOpen(false);

      toast.success("Factoring request submitted!", {
        description: "Status: pending (awaiting approval).",
        duration: 3500,
      });
    } catch (error) {
      const status = error?.response?.status;

      // Enquanto a Tarefa 3 não existir, pode dar 404.
      // Para não “travar” o fluxo, salvamos localmente e avisamos.
      if (status === 404) {
        persistFactoringDecision("accepted_local");
        setIsFactoringModalOpen(false);

        toast.info("Factoring accepted (UI).", {
          description: "Backend endpoint will be added in the next task (Task 3).",
          duration: 5000,
        });
      } else {
        toast.error("Could not submit factoring request.", {
          description: error?.response?.data?.message || "Try again later.",
          duration: 4500,
        });
      }
    } finally {
      setIsSavingFactoring(false);
    }
  };

  // Image carousel modal functions
  const openCarouselModal = (imageKey) => {
    const imageKeys = Object.keys(images[MAIN_CARD_ID]);
    const selectedIndex = imageKeys.indexOf(imageKey);
    setSelectedCarouselImageIndex(selectedIndex);
    setCarouselModalIsOpen(true);
  };

  const closeCarouselModal = () => {
    setCarouselModalIsOpen(false);
  };

  const navigateCarousel = (direction) => {
    const imageKeys = Object.keys(images[MAIN_CARD_ID]);
    const totalImages = imageKeys.length;

    if (totalImages === 0) return;

    let newIndex = selectedCarouselImageIndex;
    if (direction === "prev") {
      newIndex = (newIndex - 1 + totalImages) % totalImages;
    } else if (direction === "next") {
      newIndex = (newIndex + 1) % totalImages;
    }

    setSelectedCarouselImageIndex(newIndex);
  };

  // File handling functions
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const timestamp = Date.now();
        setImages((prevImages) => ({
          ...prevImages,
          [MAIN_CARD_ID]: {
            ...prevImages[MAIN_CARD_ID],
            [timestamp]: e.target.result,
          },
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const deleteImage = (cardId, imageKey) => {
    setImages((prevImages) => {
      const updatedImages = { ...prevImages };
      delete updatedImages[cardId][imageKey];
      return updatedImages;
    });
  };

  // Inspection diagram functions
  const handleRadioButtonChange = (value) => {
    setSelectedRadioButton(value);
  };

  const handleImageClick = (event) => {
    if (isDeliveryInspection) return;

    if (selectedRadioButton) {
      const boundingRect = event.currentTarget.getBoundingClientRect();
      const offsetX = event.clientX - boundingRect.left;
      const offsetY = event.clientY - boundingRect.top;

      const newShape = {
        type: "circle",
        position: {
          x: (offsetX / boundingRect.width) * 100,
          y: (offsetY / boundingRect.height) * 100,
        },
        value: selectedRadioButton,
      };

      setShapes((prevShapes) => [...prevShapes, newShape]);
      setUndoStack((prevUndoStack) => [...prevUndoStack, newShape]);
    }
  };

  const handleUndo = () => {
    if (undoStack.length > 0) {
      const lastShape = undoStack[undoStack.length - 1];
      setShapes((prevShapes) => prevShapes.filter((shape) => shape !== lastShape));
      setUndoStack(undoStack.slice(0, -1));
    }
  };

  // Signature functions
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  // Form validation (fluxo NORMAL – cliente presente)
  const validateFormData = () => {
    const errors = {};

    // Odometer validation (inteiro >= 0)
    if (odometer === "" || odometer === null || odometer === undefined) {
      errors.odometer = "Odometer is required";
    } else if (!isValidNonNegativeIntString(odometer)) {
      errors.odometer = "Odometer must be a non-negative integer";
    }

    // Keys validation (inteiro >= 0)
    if (keysQt === "" || keysQt === null || keysQt === undefined) {
      errors.key = "Key information is required";
    } else if (!isValidNonNegativeIntString(keysQt)) {
      errors.key = "Keys must be a non-negative integer";
    }

    // Customer information validation
    if (!customerName) {
      errors.customerName = "Customer name is required";
    }

    if (!customerPhone) {
      errors.customerPhone = "Customer phone is required";
    }

    if (!customerEmail) {
      errors.customerEmail = "Customer email is required";
    } else if (!/\S+@\S+\.\S+/.test(customerEmail)) {
      errors.customerEmail = "Please enter a valid email address";
    }

    // Check if signature exists
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      errors.signature = "Customer signature is required";
    }

    setErrors(errors);
    return errors;
  };

  // Validação para fluxo REMOTO (sem cliente presente)
  const validateRemoteFormData = () => {
    const errors = {};

    if (odometer === "" || odometer === null || odometer === undefined) {
      errors.odometer = "Odometer is required";
    } else if (!isValidNonNegativeIntString(odometer)) {
      errors.odometer = "Odometer must be a non-negative integer";
    }

    if (keysQt === "" || keysQt === null || keysQt === undefined) {
      errors.key = "Key information is required";
    } else if (!isValidNonNegativeIntString(keysQt)) {
      errors.key = "Keys must be a non-negative integer";
    }

    setErrors(errors);
    return errors;
  };

  // Helper to extract base64 from DataURL
  const extractBase64FromDataURL = (dataURL) => {
    if (!dataURL) return "";
    if (!String(dataURL).includes("data:")) {
      return dataURL;
    }
    const parts = String(dataURL).split(",");
    return parts.length > 1 ? parts[1] : dataURL;
  };

  // Load helper for canvas base image (ensures we use the SAME diagram the user saw)
  const ensureImageLoaded = (imgEl) => {
    return new Promise((resolve, reject) => {
      if (!imgEl) return reject(new Error("No image element provided"));
      if (imgEl.complete && imgEl.naturalWidth > 0) return resolve();
      const onLoad = () => {
        cleanup();
        resolve();
      };
      const onError = (e) => {
        cleanup();
        reject(e || new Error("Image failed to load"));
      };
      const cleanup = () => {
        imgEl.removeEventListener("load", onLoad);
        imgEl.removeEventListener("error", onError);
      };
      imgEl.addEventListener("load", onLoad);
      imgEl.addEventListener("error", onError);
    });
  };

  // Gera o diagrama (base64) sempre que for Pickup (Assigned/Confirmed), mesmo sem danos
  const generateInspectionDiagramBase64 = async () => {
    if (!load || !load.load_transport) return null;

    // Só gera diagrama no Pickup (Assigned=2 ou Confirmed=3)
    if (!isPickupInspection) {
      return null;
    }

    // MUST have correct base diagram
    if (!baseDiagramSrc) {
      console.error("No baseDiagramSrc resolved for current car_type.");
      return null;
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // Use the EXACT image shown to the user (diagramRef.current)
    const baseImgEl = diagramRef.current;

    try {
      if (baseImgEl) {
        await ensureImageLoaded(baseImgEl);
        canvas.width = baseImgEl.naturalWidth || 800;
        canvas.height = baseImgEl.naturalHeight || 600;

        // draw the base diagram the user is seeing
        ctx.drawImage(baseImgEl, 0, 0, canvas.width, canvas.height);
      } else {
        // fallback: load from local baseDiagramSrc (same-origin, no CORS)
        canvas.width = 800;
        canvas.height = 600;

        const img = new Image();
        img.src = baseDiagramSrc;

        await new Promise((resolve, reject) => {
          img.onload = () => {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve();
          };
          img.onerror = (e) => reject(e || new Error("Base diagram failed to load"));
        });
      }
    } catch (e) {
      console.error("Error preparing base diagram for canvas:", e);
      return null;
    }

    // Desenha danos (se houver)
    shapes.forEach((shape) => {
      ctx.beginPath();
      ctx.arc(
        (shape.position.x / 100) * canvas.width,
        (shape.position.y / 100) * canvas.height,
        20,
        0,
        2 * Math.PI
      );
      ctx.fillStyle = "#eab308";
      ctx.fill();

      ctx.fillStyle = "#000000";
      ctx.font = "bold 16px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(
        shape.value,
        (shape.position.x / 100) * canvas.width,
        (shape.position.y / 100) * canvas.height
      );
    });

    try {
      const diagramDataUrl = canvas.toDataURL("image/png");
      const b64 = extractBase64FromDataURL(diagramDataUrl);
      return b64 || null;
    } catch (e) {
      console.error("Canvas export failed (unexpected with same-origin image).", e);
      return null;
    }
  };

  // Submit normal (cliente presente)
  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    setLoading(true);

    const errors = validateFormData();
    if (Object.keys(errors).length > 0) {
      const errorMessages = Object.values(errors).join(", ");
      toast.error("Please fill out all required fields", {
        description: errorMessages,
        duration: 5000,
      });
      setLoading(false);

      const firstErrorId = Object.keys(errors)[0];
      const element = document.getElementById(firstErrorId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    try {
      const requestData = {
        load_item_id: loadItemId,
        inspection_type: isPickupInspection ? "Pickup" : "Delivery",
        odometer: odometer, // string numérica (inteiro >= 0)
        notes: notes,
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_email: customerEmail,
        drivable: toNumericString(drivable),
        intact_glass: toNumericString(intactGlass),
        paperwork: toNumericString(paperwork),
        windscreen: toNumericString(windscreen),
        keys_qt: keysQt, // string numérica (inteiro >= 0)
        spare_tire: toNumericString(spareTire),
        others: others,
      };

      if (images[MAIN_CARD_ID] && Object.keys(images[MAIN_CARD_ID]).length > 0) {
        requestData.photos = Object.values(images[MAIN_CARD_ID]).map((imageSrc) =>
          extractBase64FromDataURL(imageSrc)
        );
      }

      if (sigCanvas.current) {
        const signatureDataUrl = sigCanvas.current.toDataURL();
        requestData.customer_signature = extractBase64FromDataURL(signatureDataUrl);
      }

      // Gera diagrama SEMPRE no Pickup (Assigned/Confirmed), mesmo sem shapes
      if (isPickupInspection) {
        const diagramBase64 = await generateInspectionDiagramBase64();
        if (!diagramBase64) {
          toast.error("Inspection Diagram error", {
            description: "Could not generate the diagram image. Please reload the page and try again.",
            duration: 6000,
          });
          setLoading(false);
          return;
        }
        requestData.inspection_diagram = diagramBase64;
      }

      if (isPickupInspection) {
        localStorage.setItem("odometerValue", odometer);
      }

      const userToken = localStorage.getItem("token");
      await axios.post(`https://new.cajuu.app/api/inspectionloaditem`, requestData, {
        headers: {
          "Screen-Name": "/InspectionLoadItem",
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "application/json",
        },
      });

      toast.success("Success!", {
        description: "Inspection submitted successfully!",
        duration: 2000,
      });

      setTimeout(() => {
        navigate("/Load");
      }, 2000);
    } catch (error) {
      console.error("Error submitting form data:", error);

      let errorMessage = "Request failed";
      if (error.response && error.response.data) {
        const { message, error: errDetail, details } = error.response.data;
        if (message || errDetail) {
          errorMessage = `${message || ""} ${errDetail || ""}`.trim();
        } else if (details) {
          errorMessage = JSON.stringify(details);
        }
      }

      toast.error("Error!", {
        description: errorMessage,
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fluxo remoto (gera link de assinatura remota)
  const handleRequestRemoteSignature = async () => {
    if (!load || !load.load_transport) return;

    setIsRequestingRemoteSignature(true);
    setRemoteSignatureLink("");
    setRemoteRequested(false);

    const validationErrors = validateRemoteFormData();

    if (Object.keys(validationErrors).length > 0) {
      const errorMessages = Object.values(validationErrors).join(", ");
      toast.error("Please fill out required fields for remote inspection", {
        description: errorMessages,
        duration: 5000,
      });
      setIsRequestingRemoteSignature(false);
      return;
    }

    try {
      const requestData = {
        load_item_id: loadItemId,
        inspection_type: isPickupInspection ? "Pickup" : "Delivery",
        odometer: odometer, // string numérica (inteiro >= 0)
        notes: notes,
        drivable: toNumericString(drivable),
        intact_glass: toNumericString(intactGlass),
        paperwork: toNumericString(paperwork),
        windscreen: toNumericString(windscreen),
        keys_qt: keysQt, // string numérica (inteiro >= 0)
        spare_tire: toNumericString(spareTire),
        others: others,
      };

      if (images[MAIN_CARD_ID] && Object.keys(images[MAIN_CARD_ID]).length > 0) {
        requestData.photos = Object.values(images[MAIN_CARD_ID]).map((imageSrc) =>
          extractBase64FromDataURL(imageSrc)
        );
      }

      // Pickup: gera diagrama mesmo sem danos (obrigatório)
      if (isPickupInspection) {
        const diagramBase64 = await generateInspectionDiagramBase64();
        if (!diagramBase64) {
          toast.error("Inspection Diagram error", {
            description: "Could not generate the diagram image. Please reload the page and try again.",
            duration: 6000,
          });
          setIsRequestingRemoteSignature(false);
          return;
        }
        requestData.inspection_diagram = diagramBase64;
      }

      const userToken = localStorage.getItem("token");
      const response = await axios.post(
        `https://new.cajuu.app/api/inspectionloaditem/remote`,
        requestData,
        {
          headers: {
            "Screen-Name": "/InspectionLoadItem",
            Authorization: `Bearer ${userToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      const link = response.data.remoteSignatureLink;
      setRemoteSignatureLink(link);
      setRemoteRequested(true);

      toast.success("Remote signature link generated!", {
        description: "Copy the link below and send it to the customer via SMS, WhatsApp, or email.",
        duration: 4000,
      });
    } catch (error) {
      console.error("Error generating remote signature link:", error);

      let errorMessage = "Request failed";
      if (error.response && error.response.data) {
        const { message, error: errDetail, details } = error.response.data;
        if (message || errDetail) {
          errorMessage = `${message || ""} ${errDetail || ""}`.trim();
        } else if (details) {
          errorMessage = JSON.stringify(details);
        }
      }

      toast.error("Error generating remote link", {
        description: errorMessage,
        duration: 4000,
      });
    } finally {
      setIsRequestingRemoteSignature(false);
    }
  };

  const inputStyle = {
    "& .MuiInputBase-input": { color: "white" },
    "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.7)" },
    "& .MuiInputLabel-root": { color: "white" },
    "& .MuiInputLabel-root.Mui-focused": { color: "gray" },
    "& .MuiInputLabel-root.Mui-disabled": { color: "white" },
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "white" },
      "&:hover fieldset": { borderColor: "gray" },
      "&.Mui-focused fieldset": { borderColor: "gray" },
    },
    "& .MuiOutlinedInput-root.Mui-disabled fieldset": { borderColor: "white" },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "white",
      color: "white",
    },
    "& .MuiSelect-icon": { color: "white", opacity: 1 },
  };

  // If there's a hard diagram error, show a simple screen (no wrong fallback)
  if (diagramError) {
    return (
      <AuthenticatedLayout>
        <div className="text-white text-2xl px-4 py-6 max-w-3xl mx-auto">
          <InfoSection title="INSPECTION" description="Inspect Load" />
          <GroupedContainer className="mt-12">
            <InfoSection title="Diagram Error" />
            <div className="text-sm text-gray-300 leading-relaxed">
              <p className="mb-4">
                We could not resolve a valid diagram for this vehicle type:
              </p>
              <p className="mb-6 text-amber-400 font-semibold">
                {diagramError}
              </p>
              <div className="flex gap-3">
                <SecondaryButton
                  onClick={() => window.location.reload()}
                  className="text-amber-500 hover:text-amber-600 hover:border-amber-600"
                >
                  Reload Page
                </SecondaryButton>
                <SecondaryButton
                  onClick={() => navigate("/Load")}
                  className="text-gray-300 hover:text-white"
                >
                  Back to Loads
                </SecondaryButton>
              </div>
            </div>
          </GroupedContainer>
        </div>
      </AuthenticatedLayout>
    );
  }

  // Block rendering the form until everything is loaded (prevents wrong diagram flash)
  if (showBootLoader) {
    return (
      <AuthenticatedLayout>
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="flex flex-col items-center">
            <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center">
              <img
                src="https://view.cajuu.app/img/cajuu.png"
                alt="Cajuu logo"
                className="w-16 h-16 animate-bounce"
              />
            </div>
            <p className="mt-4 text-sm text-white">Loading, please wait...</p>
          </div>
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      {showGlobalLoader && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="flex flex-col items-center">
            <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center">
              <img
                src="https://view.cajuu.app/img/cajuu.png"
                alt="Cajuu logo"
                className="w-16 h-16 animate-bounce"
              />
            </div>
            <p className="mt-4 text-sm text-white">Processing, please wait...</p>
          </div>
        </div>
      )}

      {/* ✅ Factoring modal (Task 2) */}
      <FactoringConsentModal
        isOpen={isFactoringModalOpen}
        onRequestClose={() => setIsFactoringModalOpen(false)}
        paymentRate={paymentRate}
        paymentType={normalizePaymentType(paymentType)}
        feePercent={5}
        isSubmitting={isSavingFactoring}
        onAccept={handleAcceptFactoring}
        onDecline={handleDeclineFactoring}
      />

      <div className="text-white text-2xl px-4 py-6 max-w-7xl mx-auto">
        <InfoSection title="INSPECTION" description="Inspect Load" />

        {/* General Info Section */}
        <GroupedContainer className="mt-12">
          <InfoSection title="General Info" />

          <FormContainer>
            <FormControl
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiInputLabel-root": { color: "#fff" },
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  "& fieldset": { borderColor: "#fff" },
                  "&:hover fieldset": { borderColor: "#fff" },
                  "&.Mui-disabled fieldset": { borderColor: "#fff" },
                },
                "& .MuiSelect-icon": { color: "#fff" },
              }}
            >
              <InputLabel id="load-item">Load Item</InputLabel>
              <Select
                labelId="load-item"
                id="load-item"
                label="Load Item"
                value={load ? loadItemLabel : ""}
                disabled
                sx={inputStyle}
              >
                {load ? (
                  <MenuItem value={loadItemLabel}>{loadItemLabel}</MenuItem>
                ) : (
                  <MenuItem value="">Loading...</MenuItem>
                )}
              </Select>
            </FormControl>

            <TextField
              id="odometer"
              label="Odometer"
              value={odometer}
              onChange={(e) => setOdometer(sanitizeNonNegativeIntInput(e.target.value))}
              required
              type="text"
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                min: 0,
              }}
              error={!!errors.odometer}
              helperText={errors.odometer}
              disabled={isDeliveryInspection}
              sx={inputStyle}
            />

            <FormControl fullWidth className="mt-4">
              <InputLabel id="inspection-type" sx={{ color: "#fff" }} required>
                Inspection Type
              </InputLabel>
              <Select
                labelId="inspection-type"
                id="inspection-type"
                label="Inspection Type"
                value={
                  load?.load_transport
                    ? isPickupInspection
                      ? "Pickup"
                      : isDeliveryInspection
                      ? "Delivery"
                      : ""
                    : ""
                }
                disabled
                sx={inputStyle}
              >
                {load?.load_transport ? (
                  <MenuItem className="text-white" value={isPickupInspection ? "Pickup" : "Delivery"}>
                    {isPickupInspection ? "Pickup" : "Delivery"}
                  </MenuItem>
                ) : (
                  <MenuItem value="">Loading...</MenuItem>
                )}
              </Select>
            </FormControl>
          </FormContainer>
        </GroupedContainer>

        {/* Inspection Diagram Section */}
        <GroupedContainer className="mt-12">
          <InfoSection title="Inspection Diagram" />

          <div className="flex justify-center w-full">
            <div className="mt-5 w-full flex flex-col items-center">
              <div id="image-container" className="relative w-full" onClick={handleImageClick}>
                <img
                  ref={diagramRef}
                  src={diagramToDisplay || "/placeholder.svg"}
                  alt="Inspection Diagram"
                  className="w-full rounded-2xl border-2 border-gray-700"
                />

                {shapes.map((shape, index) => (
                  <div
                    key={`shape-${index}`}
                    className="absolute"
                    style={{
                      left: `calc(${shape.position.x}% - ${windowWidth < 768 ? 2 : 4}px)`,
                      top: `calc(${shape.position.y}% - ${windowWidth < 768 ? 2 : 4}px)`,
                    }}
                  >
                    <div
                      className={`flex items-center justify-center ${
                        windowWidth < 768 ? "w-4 h-4 text-xs" : "w-8 h-8 text-sm"
                      } bg-amber-500 text-black rounded-full font-bold`}
                    >
                      {shape.value}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap justify-center mb-4 mt-5 gap-2">
                {!isDeliveryInspection &&
                  radioOptions.map((option) => (
                    <div key={option.value} className="flex items-center justify-center">
                      <input
                        type="radio"
                        id={`radio-${option.value}`}
                        name="radio-button"
                        value={option.value}
                        className="sr-only"
                        onChange={() => handleRadioButtonChange(option.value)}
                        disabled={isDeliveryInspection}
                      />
                      <label
                        htmlFor={`radio-${option.value}`}
                        className={`flex text-center flex-col items-center justify-center w-[5rem] h-[5rem] rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                          selectedRadioButton === option.value
                            ? "bg-amber-500 border-amber-600 text-black"
                            : "bg-[#1c1b19] border-[#1c1b19] text-white hover:bg-[#2c2b29]"
                        } ${isDeliveryInspection ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        <div className="text-lg font-bold">{option.abbreviation}</div>
                        <div className="text-xs mt-1">{option.label}</div>
                      </label>
                    </div>
                  ))}
              </div>

              {!isDeliveryInspection && (
                <div className="flex justify-center w-full max-w-md">
                  <SecondaryButton
                    onClick={handleUndo}
                    disabled={shapes.length === 0}
                    className="bg-rose-600 hover:bg-rose-700 hover:text-white border-none h-[3rem] text-white w-full justify-center mt-4 transition-transform duration-200 ease-in-out hover:scale-95 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Undo size={18} />
                    Undo
                  </SecondaryButton>
                </div>
              )}
            </div>
          </div>
        </GroupedContainer>

        {/* Photos Of Vehicle Section */}
        <div className="mt-12">
          <InfoSection title="Photos Of Vehicle" description="Import photos" />

          <GroupedContainer className="w-full ">
            <div
              className="flex flex-col w-full h-[15rem] justify-center items-center p-6 border-2 border-dashed border-gray-600 rounded-xl bg-[#1c1b19] hover:bg-[#2c2b29] transition-colors cursor-pointer"
              onClick={() => fileInputRef.current.click()}
            >
              <Upload size={48} className="text-amber-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Drop photos of Vehicle here</h3>
              <p className="text-sm text-gray-400 text-center">
                Click to upload or drag and drop your images here (Max 4 MB)
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                className="hidden"
              />
            </div>

            {/* Display uploaded images */}
            {Object.keys(images[MAIN_CARD_ID] || {}).length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-medium mb-3">Uploaded Images</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Object.entries(images[MAIN_CARD_ID] || {}).map(([key, url]) => (
                    <div key={key} className="relative group">
                      <img
                        src={url || "/placeholder.svg"}
                        alt={`Vehicle ${key}`}
                        className="w-full h-32 object-cover rounded-lg cursor-pointer"
                        onClick={() => openCarouselModal(key)}
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteImage(MAIN_CARD_ID, key);
                        }}
                        className="absolute top-2 right-2 bg-black bg-opacity-70 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={16} className="text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </GroupedContainer>

          {/* Image Carousel Modal */}
          <Modal
            isOpen={carouselModalIsOpen}
            onRequestClose={closeCarouselModal}
            contentLabel="Image Carousel"
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            overlayClassName="fixed inset-0 bg-black bg-opacity-75"
          >
            <div className="bg-[#1c1b19] rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex justify-between items-center p-4 border-b border-gray-700">
                <h2 className="text-xl font-bold">Vehicle Photos</h2>
                <button onClick={closeCarouselModal} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <div className="relative flex-grow flex items-center justify-center bg-black p-4">
                {Object.keys(images[MAIN_CARD_ID] || {}).length > 0 ? (
                  <>
                    <img
                      src={Object.values(images[MAIN_CARD_ID])[selectedCarouselImageIndex] || "/placeholder.svg"}
                      alt="Vehicle"
                      className="max-h-[70vh] max-w-full object-contain"
                    />

                    <button
                      onClick={() => navigateCarousel("prev")}
                      className="absolute left-4 bg-black bg-opacity-50 rounded-full p-2 text-white hover:bg-opacity-70"
                    >
                      <ChevronLeft size={24} />
                    </button>

                    <button
                      onClick={() => navigateCarousel("next")}
                      className="absolute right-4 bg-black bg-opacity-50 rounded-full p-2 text-white hover:bg-opacity-70"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </>
                ) : (
                  <p className="text-gray-400">No images to display</p>
                )}
              </div>

              <div className="p-4 border-t border-gray-700">
                <p className="text-sm text-gray-400">
                  {Object.keys(images[MAIN_CARD_ID] || {}).length > 0
                    ? `Image ${selectedCarouselImageIndex + 1} of ${Object.keys(images[MAIN_CARD_ID]).length}`
                    : "No images"}
                </p>
              </div>
            </div>
          </Modal>
        </div>

        {/* Notes Section */}
        <GroupedContainer className="mt-12">
          <InfoSection title="Notes" />

          <div className="w-full">
            <TextField
              id="notes"
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              focused
              multiline
              rows={9}
              color="secondary"
              className="w-full"
            />
          </div>
        </GroupedContainer>

        {/* Additional Information Section */}
        <GroupedContainer className="mt-12">
          <InfoSection title="Additional Information" />

          <FormContainer grid={4}>
            <FormControl fullWidth className="mt-4">
              <InputLabel id="drivable">Drivable</InputLabel>
              <Select
                labelId="drivable"
                id="drivable"
                value={drivable}
                onChange={(e) => setDrivable(e.target.value)}
                label="Drivable"
                focused
                error={!!errors.drivable}
                disabled={isDeliveryInspection}
                sx={inputStyle}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
              {errors.drivable && <p className="text-red-500 text-xs mt-1">{errors.drivable}</p>}
            </FormControl>

            <FormControl fullWidth className="mt-4">
              <InputLabel id="windscreen">Windscreen</InputLabel>
              <Select
                labelId="windscreen"
                id="windscreen"
                value={windscreen}
                onChange={(e) => setWindscreen(e.target.value)}
                label="Windscreen"
                focused
                error={!!errors.windscreen}
                disabled={isDeliveryInspection}
                sx={inputStyle}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
              {errors.windscreen && <p className="text-red-500 text-xs mt-1">{errors.windscreen}</p>}
            </FormControl>

            <FormControl fullWidth className="mt-4">
              <InputLabel id="intact-glass">Intact Glass</InputLabel>
              <Select
                labelId="intact-glass"
                id="intact-glass"
                value={intactGlass}
                onChange={(e) => setIntactGlass(e.target.value)}
                label="Intact Glass"
                focused
                error={!!errors.intactGlass}
                disabled={isDeliveryInspection}
                sx={inputStyle}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
              {errors.intactGlass && <p className="text-red-500 text-xs mt-1">{errors.intactGlass}</p>}
            </FormControl>

            <FormControl fullWidth className="mt-4">
              <InputLabel id="paper-work">Paper Work</InputLabel>
              <Select
                labelId="paper-work"
                id="paper-work"
                value={paperwork}
                onChange={(e) => setPaperwork(e.target.value)}
                label="Paper Work"
                focused
                error={!!errors.paperWork}
                disabled={isDeliveryInspection}
                sx={inputStyle}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
              {errors.paperWork && <p className="text-red-500 text-xs mt-1">{errors.paperWork}</p>}
            </FormControl>
          </FormContainer>
        </GroupedContainer>

        {/* Loose Items Section */}
        <GroupedContainer className="mt-12">
          <InfoSection title="Loose Items" />

          <FormContainer className="md:grid-cols-3 lg:grid-cols-3">
            <TextField
              id="key"
              label="Key"
              value={keysQt}
              onChange={(e) => setKeysQt(sanitizeNonNegativeIntInput(e.target.value))}
              focused
              required
              color="secondary"
              fullWidth
              type="text"
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                min: 0,
              }}
              error={!!errors.key}
              helperText={errors.key}
              disabled={isDeliveryInspection}
              sx={inputStyle}
            />

            <FormControl fullWidth className="mt-4">
              <InputLabel id="spare-tire">Spare Tire</InputLabel>
              <Select
                labelId="spare-tire"
                id="spare-tire"
                value={spareTire}
                onChange={(e) => setSpareTire(e.target.value)}
                label="Spare Tire"
                focused
                error={!!errors.spareTire}
                disabled={isDeliveryInspection}
                sx={inputStyle}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
              {errors.spareTire && <p className="text-red-500 text-xs mt-1">{errors.spareTire}</p>}
            </FormControl>

            <TextField
              id="others"
              label="Others"
              value={others}
              onChange={(e) => setOthers(e.target.value)}
              focused
              color="secondary"
              fullWidth
              disabled={isDeliveryInspection}
              sx={inputStyle}
            />
          </FormContainer>
        </GroupedContainer>

        {/* Signature Section */}
        <GroupedContainer className="mt-12">
          <InfoSection title="Signature" />

          {/* Botão de Assinatura Remota ANTES dos campos do Customer */}
          <div className="flex flex-col items-center mb-6 w-full">
            <p className="text-sm text-gray-400 mb-3 text-center">
              Customer not present? Generate a remote signature link and send it by SMS, WhatsApp or email.
            </p>
            <SecondaryButton
              onClick={handleRequestRemoteSignature}
              disabled={isRequestingRemoteSignature || loading || remoteRequested}
              className="flex items-center justify-center gap-2 text-amber-500 hover:text-amber-600 hover:border-amber-600 font-medium py-2 px-4 rounded-md w-full max-w-xs transition-colors duration-200 disabled:opacity-60"
            >
              {isRequestingRemoteSignature ? (
                "Generating link..."
              ) : (
                <>
                  <Send size={18} />
                  {remoteRequested ? "Remote link generated" : "Request Remote Signature"}
                </>
              )}
            </SecondaryButton>
          </div>

          {/* Link gerado para assinatura remota */}
          {remoteRequested && remoteSignatureLink && (
            <div className="flex flex-col items-center mb-1 w-full">
              <div className="mb-6 w-full max-w-xl bg-emerald-500/10 border border-emerald-500/40 text-emerald-100 rounded-xl p-4">
                <p className="text-xs text-gray-400 mb-2">Copy and share this link with the customer:</p>
                <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={remoteSignatureLink}
                    className="flex-1 bg-transparent text-xs text-gray-100 border border-gray-700 rounded px-2 py-1"
                    onFocus={(e) => e.target.select()}
                  />
                  <SecondaryButton
                    onClick={() => {
                      navigator.clipboard
                        .writeText(remoteSignatureLink)
                        .then(() =>
                          toast.success("Link copied to clipboard!", {
                            duration: 2000,
                          })
                        )
                        .catch(() => toast.error("Could not copy link to clipboard."));
                    }}
                    className="text-xs px-3 py-1 hover:text-amber-600 hover:border-amber-600"
                  >
                    Copy
                  </SecondaryButton>
                </div>
              </div>
            </div>
          )}

          <hr className="border-t-1 border-gray-600 mr-3 w-full" />

          {/* Campos do Customer + Signature presencial – só se NÃO for remoto */}
          {!remoteRequested && (
            <>
              <FormContainer>
                <TextField
                  id="customerName"
                  label="Customer Name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  focused
                  required
                  color="secondary"
                  error={!!errors.customerName}
                  helperText={errors.customerName}
                />

                <TextField
                  id="customerPhone"
                  label="Customer Phone"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  focused
                  required
                  color="secondary"
                  error={!!errors.customerPhone}
                  helperText={errors.customerPhone}
                />

                <TextField
                  id="customerEmail"
                  label="Customer Email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  focused
                  required
                  color="secondary"
                  type="email"
                  error={!!errors.customerEmail}
                  helperText={errors.customerEmail}
                />
              </FormContainer>

              {/* Customer Signature Presencial */}
              <div className="mt-8 w-full">
                <label className="block text-lg font-medium text-gray-200 mb-2">
                  Customer Signature
                </label>
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor="white"
                  canvasProps={{
                    height: 200,
                    className: `w-full border-dashed border-2 rounded-xl ${
                      errors.signature ? "border-red-500" : "border-gray-600"
                    } bg-[#1c1b19]`,
                  }}
                />
                {errors.signature && <p className="text-red-500 text-sm mt-1">{errors.signature}</p>}
                <div className="flex justify-end mt-2">
                  <SecondaryButton onClick={clearSignature} className="text-gray-400 hover:text-white text-sm">
                    Clear Signature
                  </SecondaryButton>
                </div>
              </div>
            </>
          )}
        </GroupedContainer>

        {/* Botão Submit – só aparece se NÃO estiver usando assinatura remota */}
        {!remoteRequested && (
          <div className="flex justify-end mb-16 mt-10">
            <SecondaryButton
              onClick={handleSubmit}
              disabled={loading}
              className={`${
                loading ? "bg-amber-400" : "bg-amber-500 hover:bg-amber-600 hover:text-black"
              } border-none h-[3rem] text-slate-900 font-bold w-full max-w-md justify-center transition-transform duration-200 ease-in-out hover:scale-95 active:scale-95 flex items-center gap-2`}
            >
              {loading ? "Submitting..." : "Submit Inspection"}
            </SecondaryButton>
          </div>
        )}
      </div>
    </AuthenticatedLayout>
  );
}

export default Inspection;